import React from "react";
import { CheckCircle } from "lucide-react";
import HeroButton from "./hero/HeroButton";

const features = [
  <>
    <strong>Website conversion rates increase</strong> — visitors who would have
    bounced become qualified leads
  </>,
  <>
    <strong>Higher-intent implant leads captured</strong> — patients who engage
    with a smile simulation are pre-qualified for high-value treatment
  </>,
  <>
    <strong>Full-arch treatment is pre-framed</strong> — patients arrive at
    consultations already excited about All-on-4 or All-on-6 results
  </>,
  <>
    <strong>More All-on-X revenue from existing traffic</strong> — no additional
    ad spend required to generate new implant opportunities
  </>,
];

/* ─── Floating Stat Card ──────────────────────────────────────── */
const StatCard = () => (
  <div
    className="absolute -left-4 md:-left-8 top-[25%] md:top-[35%] z-20 bg-white rounded-2xl shadow-[0_16px_40px_rgba(15,111,222,0.14)] border border-slate-100 px-4 py-3 md:px-5 md:py-4 w-[160px] md:w-[190px] select-none"
    style={{ backdropFilter: "blur(8px)" }}
  >
    <p className="text-[10px] md:text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1 md:mb-1.5">
      Case Acceptance Rate
    </p>
    <div className="flex items-center gap-2 md:gap-2.5">
      <span className="text-[24px] md:text-[30px] font-bold text-slate-900 leading-none">
        60%+
      </span>
      <span className="inline-flex items-center gap-0.5 bg-emerald-50 text-emerald-600 text-[10px] md:text-[12px] font-bold px-1.5 md:px-2 py-0.5 rounded-full border border-emerald-100">
        <svg
          width="8"
          height="8"
          viewBox="0 0 9 9"
          fill="none"
          className="shrink-0 md:w-[9px] md:h-[9px]"
        >
          <path
            d="M4.5 7.5V1.5M4.5 1.5L1.5 4.5M4.5 1.5L7.5 4.5"
            stroke="#10b981"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        12.5%
      </span>
    </div>
  </div>
);

/* ─── Testimonial / Quote Card ────────────────────────────────── */
const TestimonialCard = () => (
  <div
    className="absolute bottom-2 md:bottom-10 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3 rounded-2xl px-4 md:px-6 py-4 md:py-5 shadow-[0_16px_40px_rgba(26,111,255,0.28)] max-w-[320px]"
    style={{
      background: "linear-gradient(135deg, #1a6fff 0%, #0051d4 100%)",
    }}
  >
    {/* Avatar */}
    <p className="text-white text-[10px] md:text-[18px] font-semibold leading-snug">
      You turn them into visual,{" "}
      <span className="italic font-bold">emotionally invested</span> leads.
    </p>
    <div className="w-10 h-10 md:w-14 md:h-14 rounded-full border-2 border-white/40 overflow-hidden flex-shrink-0 shadow-md">
      <img
        src="/middle_aged.jpeg"
        alt="Practice owner"
        width="56"
        height="56"
        loading="lazy"
        className="w-full h-full object-cover"
      />
    </div>
  </div>
);

/* ─── Main Interface Mockup ───────────────────────────────────── */
const InterfaceMockup = () => (
  /* Outer shell — wide enough for the bleed of floating cards */
  <div className="relative w-full h-full flex items-center justify-center">
    {/* Soft background glow */}
    <div className="absolute inset-0 -z-10 rounded-[40px] bg-[#dbeeff]/50 blur-3xl scale-90 pointer-events-none" />

    {/* Main interface card */}
    <div className="relative z-10 rounded-[28px] border-[12px] md:border-[18px] border-blue-100/80 bg-white shadow-[0_24px_64px_rgba(26,111,255,0.15)] overflow-hidden">
      <img
        src="/begin_virtual.webp"
        alt="SmileReveal virtual consultation interface"
        width="800"
        height="1200"
        loading="lazy"
        className="w-full h-auto object-cover object-top"
        draggable={false}
      />
    </div>

    {/* Floating cards — rendered OUTSIDE the clipped card so they bleed */}
    <StatCard />
    <TestimonialCard />
  </div>
);

/* ─── Main Section ────────────────────────────────────────────── */
export default function LeadMachine() {
  return (
    <section className="relative py-20 lg:py-28 px-6 sm:px-12 lg:px-20 max-w-7xl mx-auto overflow-visible">
      {/* Subtle grid texture */}
      <div
        className="absolute inset-0 -z-10 opacity-[0.15] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(#cbd5e1 1px, transparent 1px), linear-gradient(90deg, #cbd5e1 1px, transparent 1px)",
          backgroundSize: "60px 60px",
          maskImage:
            "radial-gradient(ellipse at 60% 50%, black 30%, transparent 80%)",
        }}
      />

      <h2 className="lg:hidden text-[32px] sm:text-[36px] font-bold tracking-tight text-slate-900 leading-[1.08] mb-6">
        Turn Your Dental Website Into an All-on-X Lead Generation Machine
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-[46%_54%] gap-12 lg:gap-20 items-center">
        {/* ── LEFT: Text column ─────────────────────────────────── */}
        <div className="order-2 lg:order-1">
          <h2 className="hidden lg:block text-[32px] sm:text-[36px] lg:text-[42px] font-bold tracking-tight text-slate-900 leading-[1.08] mb-6">
            Turn Your Dental Website Into an All-on-X Lead Generation Machine
          </h2>

          <p className="text-slate-500 text-[15px] sm:text-[14px] leading-relaxed font-medium mb-3">
            SmileReveal™ isn't just for chairside consultations — it's a dental
            website lead capture tool that converts abandoning visitors into
            qualified implant and All-on-X opportunities.
          </p>
          <p className="text-slate-500 text-[15px] sm:text-[14px] leading-relaxed font-medium mb-8 max-w-[480px]">
            Install SmileReveal as an exit-intent widget on your practice
            website. When visitors are about to leave, they see an instant AI
            smile preview that hooks their attention and captures their contact
            info.
          </p>

          {/* Feature list header */}
          <p className="text-slate-800 font-semibold text-[15.5px] mb-4">
            When SmileReveal™ is installed on your dental practice website:
          </p>

          {/* Feature rows */}
          <div className="space-y-2.5 mb-10">
            {features.map((item, idx) => (
              <div
                key={idx}
                className="flex items-start gap-3.5 rounded-[13px] border border-slate-200 bg-white/80 px-4 py-3.5 shadow-[0_1px_4px_rgba(0,0,0,0.04)]"
              >
                <CheckCircle
                  className="w-5 h-5 text-[#1a6fff] flex-shrink-0 mt-0.5"
                  strokeWidth={2.2}
                />
                <span className="text-slate-700 text-[13px] font-medium">
                  {item}
                </span>
              </div>
            ))}
          </div>

          {/* CTA button */}
          <HeroButton
            href="https://app.smilereveal.io"
            className="w-full lg:w-fit"
          >
            Get Started
          </HeroButton>
        </div>

        {/* ── RIGHT: Visual column ──────────────────────────────── */}
        <div className="order-1 lg:order-2 relative mt-0 h-[520px] sm:h-[680px] lg:h-[840px] xl:h-[900px] overflow-visible ">
          <InterfaceMockup />
        </div>
      </div>
    </section>
  );
}
